import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { DialogType, SwitchAccountJourneyPoint } from '../../common/enums.js';
import { closeMsg, confirmMsg, logOutMsg, subChangePendingMsg, t } from '../../common/i18n/index.js';
import { SupportCase } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useDialog } from '../../public/site/DialogProvider.js';
import { closeDialog } from '../../public/site/dialogStore.js';
import { ChangeSimCardConfirmDialog } from '../ChangeSimCardConfirmDialog/ChangeSimCardConfirmDialog.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { EppTerminateDialog } from '../EppDialogs/EppTerminateDialog.js';
import { EppRedeemDialog } from '../EppRedeem/EppRedeemDialog.js';
import { ReviewOnlineOrderDialog } from '../ReviewOnlineOrderDialog/ReviewOnlineOrderDialog.js';
import { SupportCaseDialog } from '../SupportCaseDialog/SupportCaseDialog.js';
import { EmployeeYttContactChangeDialog } from '../YttContactChangeDialog/EmployeeYttContactChangeDialog.js';
import type { GenericConfirmationDialogParams, SwitchAccountDialogParams } from '../../common/types/dialog.js';

export const EoeDialog = () => {
  const { logout, validateAuth, switchAccount } = useAuth();
  const navigate = useNavigate();
  const dialog = useDialog();
  const onCloseDialog = () => closeDialog();

  if (dialog?.params?.type) {
    switch (dialog.params.type) {
      case DialogType.ADMIN_FORBIDDEN:
        return (
          <DialogWrapper
            header={t.LQ3X(logOutMsg)}
            onCloseDialog={onCloseDialog}
            buttons={[{ onClick: () => logout(), text: t.LQ3X(logOutMsg) }]}
          >
            <p>
              {t.KDZD(
                'It seems that you are an admin user and logged in from OmaElisa. Please log out and register your ElisaID (if not done already) and then log into Employee Portal. Click confirm to log out of OmaElisa.'
              )}
            </p>
          </DialogWrapper>
        );

      case DialogType.EPP_TERMINATE:
        return (
          <EppTerminateDialog
            params={dialog.params}
            onCloseDialog={onCloseDialog}
            isLoading={dialog.submitInProgress}
            errors={dialog.errors}
            isEmployee={true}
          />
        );

      case DialogType.EPP_REDEEM:
        return (
          <EppRedeemDialog
            onCloseDialog={onCloseDialog}
            params={dialog.params}
            errors={dialog.errors}
            submitInProgress={dialog.submitInProgress}
          />
        );

      case DialogType.SUBSCRIPTION_ACTION_PENDING:
        return (
          <DialogWrapper closeable header={t.I9KT('Change order in progress')} onCloseDialog={onCloseDialog}>
            {t.MQHO(subChangePendingMsg)}
          </DialogWrapper>
        );

      case DialogType.CHANGE_SIM_CARD_CONFIRMATION:
        return (
          <ChangeSimCardConfirmDialog
            category={dialog.params.category}
            isEoe={true}
            simCardNumber={dialog.params.simCardNumber}
            sub={dialog.params.sub}
            onCloseDialog={onCloseDialog}
            isLoading={dialog.submitInProgress}
            errors={dialog.errors}
          />
        );

      case DialogType.GENERIC_INFO_DIALOG:
        return (
          <DialogWrapper
            buttons={[{ onClick: onCloseDialog, text: t.WOYD(closeMsg) }]}
            header={dialog.params.header}
            onCloseDialog={onCloseDialog}
            size={dialog.params.wide ? 'l' : 'm'}
          >
            <div className={dsClass.PADDING_TOP_4}>{dialog.params.body}</div>
          </DialogWrapper>
        );

      case DialogType.DUPLICATE_CONTACT:
        return (
          <DialogWrapper
            buttons={[
              {
                onClick: () => {
                  onCloseDialog();
                  validateAuth();
                },
                text: 'OK',
              },
            ]}
            onCloseDialog={onCloseDialog}
            header={dialog.params.header}
          >
            <p>{dialog.params.body}</p>
          </DialogWrapper>
        );

      case DialogType.CONFLICTING_CONTACT:
        return (
          <DialogWrapper
            buttons={[
              {
                onClick: () => {
                  onCloseDialog();
                  validateAuth();
                },
                text: 'OK',
              },
            ]}
            onCloseDialog={onCloseDialog}
            header={t.M3ZW('Mobile phone number or email cannot be updated')}
          >
            <p>
              {t.L9OE(
                'Please contact our customer service at 010 80 8088 (Monday to Friday, 8:00 AM to 4:30 PM). We apologize for the inconvenience.'
              )}
            </p>
          </DialogWrapper>
        );

      case DialogType.GENERIC_CONFIRMATION_DIALOG:
        return (
          <ConfirmationDialog
            body={dialog.params.body}
            confirmButtonText={dialog.params.confirmButtonText}
            errors={dialog.errors}
            header={dialog.params.header}
            hideClosingCross={dialog.params.hideClosingCross}
            isLoading={dialog.submitInProgress}
            onCloseDialog={() => {
              const params = dialog.params as GenericConfirmationDialogParams;
              if (params.onCancel) {
                params.onCancel();
              }
              onCloseDialog();
            }}
            onConfirm={dialog.params.onConfirm}
          />
        );

      case DialogType.SWITCH_ACCOUNT:
        return (
          <ConfirmationDialog
            body={dialog.params.body}
            confirmButtonText={t.QVYK(confirmMsg)}
            errors={dialog.errors}
            header={dialog.params.header}
            isLoading={dialog.submitInProgress}
            onCloseDialog={() => {
              const params = dialog.params as SwitchAccountDialogParams;
              if (params.journeyPoint === SwitchAccountJourneyPoint.MOBILE_SUB_PAIRING) {
                navigate(paths.EMPLOYEE_VOICE_SUBSCRIPTION_ADD);
              } else if (params.journeyPoint === SwitchAccountJourneyPoint.ONBOARDING) {
                validateAuth();
              }
              onCloseDialog();
            }}
            onConfirm={() => switchAccount()}
          />
        );

      case DialogType.REVIEW_ONLINE_ORDER:
        return (
          <ReviewOnlineOrderDialog
            onlineOrderReview={dialog.params.onlineOrderReview}
            customerOrder={dialog.params.customerOrder}
            onCloseDialog={onCloseDialog}
            approve={dialog.params.approved}
            errors={dialog.errors}
            ordererEmail={dialog.params.ordererEmail}
            isLoading={dialog.submitInProgress}
            isEoe={true}
          />
        );

      case DialogType.EPP_REDEEM_REJECT:
        return (
          <SupportCaseDialog
            action="EPP_REDEEM_REJECT"
            body=""
            errors={dialog.errors}
            featureName={SupportCase.FeatureEnum.SUBSCRIPTIONS}
            header={t.W692('Confirm redeem rejection request')}
            isSubmitInProgress={dialog.submitInProgress}
            onCloseDialog={onCloseDialog}
            onSubmitDialog={dialog.params.onRedeemReject}
            placeholder={t.EBNB('Additional info to employee')}
            saveButtonLabel={t.QVYK(confirmMsg)}
          />
        );

      case DialogType.UPDATE_USER_YTT_WARNING: {
        return (
          <EmployeeYttContactChangeDialog
            onCloseDialog={onCloseDialog}
            values={dialog.params.values}
            onConfirm={dialog.params.onConfirm}
            validationErrors={dialog.params.validationErrors}
          />
        );
      }

      case DialogType.GENERIC_EMPTY_DIALOG:
        return (
          <DialogWrapper
            closeable
            header={dialog.params.header}
            onCloseDialog={onCloseDialog}
            size={dialog.params.wide ? 'l' : 'm'}
          >
            <div className={dsClass.PADDING_TOP_4}>{dialog.params.body}</div>
          </DialogWrapper>
        );
    }
  }
  return null;
};
